import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Smartphone } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

const AppDownloadLinksSection = () => {
  const { t } = useTranslation();

  const [iosLink, setIosLink] = useState('');
  const [androidLink, setAndroidLink] = useState('');
  const [macLink, setMacLink] = useState('');
  const [windowsLink, setWindowsLink] = useState('');
  const [downloadPageLink, setDownloadPageLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const data = await api.get<{ value: string }>(
        '/backoffice/content/appDownloadLinks',
      );
      const parsed = JSON.parse(data.value);
      setIosLink(parsed.ios || '');
      setAndroidLink(parsed.android || '');
      setMacLink(parsed.mac || '');
      setWindowsLink(parsed.windows || '');
      setDownloadPageLink(parsed.downloadPage || '');
      setDirty(false);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);

    try {
      await api.put('/backoffice/content/appDownloadLinks', {
        value: JSON.stringify({
          ios: iosLink,
          android: androidLink,
          mac: macLink,
          windows: windowsLink,
          downloadPage: downloadPageLink,
        }),
      });
      setDirty(false);
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const markDirty = () => setDirty(true);

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-5">
          <Smartphone className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.app_download_links')}
          </h3>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              iOS
            </label>
            <Input
              type="url"
              value={iosLink}
              onChange={(e) => {
                setIosLink(e.target.value);
                markDirty();
              }}
              placeholder={t('config.ios_link_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Android
            </label>
            <Input
              type="url"
              value={androidLink}
              onChange={(e) => {
                setAndroidLink(e.target.value);
                markDirty();
              }}
              placeholder={t('config.android_link_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              macOS
            </label>
            <Input
              type="url"
              value={macLink}
              onChange={(e) => {
                setMacLink(e.target.value);
                markDirty();
              }}
              placeholder={t('config.mac_link_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Windows
            </label>
            <Input
              type="url"
              value={windowsLink}
              onChange={(e) => {
                setWindowsLink(e.target.value);
                markDirty();
              }}
              placeholder={t('config.windows_link_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('config.download_page')}
            </label>
            <Input
              type="url"
              value={downloadPageLink}
              onChange={(e) => {
                setDownloadPageLink(e.target.value);
                markDirty();
              }}
              placeholder={t('config.download_page_placeholder')}
            />
          </div>
        </div>

        <Actions
          onCancel={load}
          onSave={handleSave}
          saving={saving}
          disabled={!dirty}
        />
      </CardContent>
    </Card>
  );
};

export default AppDownloadLinksSection;
